import { useId, useState, type FormEvent, type ReactNode } from "react";
import { ErrorNote } from "./ErrorNote";
import { Sheet } from "./Sheet";

interface ActionProps {
  title: string;
  /** Label of the button that carries out the action. */
  action: string;
  /** The action deletes or removes something. */
  danger?: boolean;
  onClose: () => void;
}

/** Asks before an action, in place of the browser's confirm. */
export function ConfirmDialog(props: ActionProps & { children: ReactNode; onConfirm: () => Promise<unknown> }) {
  return (
    <ActionSheet {...props} onSubmit={props.onConfirm} focusAction>
      {props.children}
    </ActionSheet>
  );
}

/** Asks for one value, in place of the browser's prompt; `validate` returns why a value is not accepted. */
export function InputDialog(
  props: ActionProps & {
    label: string;
    type?: "text" | "password";
    autoComplete?: string;
    children?: ReactNode;
    validate?: (value: string) => string | null;
    onConfirm: (value: string) => Promise<unknown>;
  },
) {
  const [value, setValue] = useState("");
  const submit = async () => {
    const problem = props.validate?.(value);
    if (problem) throw new Error(problem);
    await props.onConfirm(value);
  };
  return (
    <ActionSheet {...props} onSubmit={submit}>
      {props.children}
      <label className="field">
        <span>{props.label}</span>
        <input type={props.type ?? "text"} autoComplete={props.autoComplete} autoFocus value={value} onChange={(e) => setValue(e.target.value)} />
      </label>
    </ActionSheet>
  );
}

/**
 * A sheet whose body is a form: Enter submits, Escape cancels, focus returns to the opener on closing.
 * A failed action keeps the sheet open and shows the reason.
 */
function ActionSheet(props: ActionProps & { children: ReactNode; onSubmit: () => Promise<unknown>; focusAction?: boolean }) {
  const formId = useId();
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const close = () => {
    props.onClose();
    opener?.focus();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await props.onSubmit();
      close();
    } catch (failure) {
      setError(failure instanceof Error ? failure : new Error(String(failure)));
      setBusy(false);
    }
  };

  return (
    <Sheet
      title={props.title}
      onClose={close}
      footer={
        <>
          <button type="button" className="button secondary" onClick={close}>
            Abbrechen
          </button>
          <button type="submit" form={formId} className={`button ${props.danger ? "danger" : "primary"}`} disabled={busy} autoFocus={props.focusAction}>
            {props.action}
          </button>
        </>
      }
    >
      <form id={formId} className="stack" noValidate onSubmit={(e) => void submit(e)}>
        {props.children}
        <ErrorNote error={error} />
      </form>
    </Sheet>
  );
}
